import { useState } from "react";
import { Header } from "../components/layout/Header";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "../components/ui/Icons";
import { CoreConceptsDocs } from "./docs/CoreConceptsDocs";
import { MarketplacesDocs } from "./docs/MarketplacesDocs";
import { TechnicalReferenceDocs } from "./docs/TechnicalReferenceDocs";
import { ApiReferenceDocs } from "./docs/ApiReferenceDocs";
import { AgentApiDocs } from "./docs/AgentApiDocs";
import { SuperbotCardsDocs } from "./docs/SuperbotCardsDocs";

type SectionGroupId =
  | "core-concepts"
  | "marketplaces"
  | "technical-reference"
  | "api-reference"
  | "agent-api"
  | "superbot-cards";

type Section =
  | "overview"
  | "marketplace-file"
  | "schema"
  | "sources"
  | "hosting"
  | "troubleshooting"
  | "what-is-plugin"
  | "what-is-command"
  | "what-is-agent"
  | "what-is-skill"
  | "what-is-hook"
  | "what-is-mcp"
  | "how-they-work-together"
  | "reference-overview"
  | "components-reference"
  | "installation-scopes"
  | "manifest-schema"
  | "caching-resolution"
  | "directory-structure"
  | "cli-commands"
  | "debugging"
  | "api-overview"
  | "api-auth"
  | "api-marketplaces"
  | "api-marketplace-plugins"
  | "api-public"
  | "api-agent-workflow"
  | "agent-quick-start"
  | "agent-auth"
  | "agent-plugins-public"
  | "agent-import"
  | "agent-plugins-auth"
  | "agent-favorites"
  | "agent-marketplaces-auth"
  | "agent-marketplaces-public"
  | "agent-packs"
  | "agent-skill-creator"
  | "agent-workflow"
  | "cards-overview"
  | "cards-superbot-json"
  | "cards-quick-start"
  | "cards-card-section"
  | "cards-settings"
  | "cards-schedule"
  | "cards-agent"
  | "cards-data-flow"
  | "cards-full-example"
  | "cards-api"
  | "cards-migration";

type SectionGroup = {
  id: SectionGroupId;
  title: string;
  description: string;
  sections: { id: Section; title: string }[];
};

const sectionGroups: SectionGroup[] = [
  {
    id: "core-concepts",
    title: "Core Concepts",
    description: "Learn what plugins, commands, agents, skills, and hooks are",
    sections: [
      { id: "what-is-plugin", title: "What is a Plugin?" },
      { id: "what-is-command", title: "What is a Command?" },
      { id: "what-is-agent", title: "What is an Agent?" },
      { id: "what-is-skill", title: "What is a Skill?" },
      { id: "what-is-hook", title: "What is a Hook?" },
      { id: "what-is-mcp", title: "What is MCP?" },
      { id: "how-they-work-together", title: "How They Work Together" },
    ],
  },
  {
    id: "marketplaces",
    title: "Marketplaces",
    description: "Create and distribute plugin marketplaces",
    sections: [
      { id: "overview", title: "Overview" },
      { id: "marketplace-file", title: "Create the Marketplace File" },
      { id: "schema", title: "Marketplace Schema" },
      { id: "sources", title: "Plugin Sources" },
      { id: "hosting", title: "Host & Distribute" },
      { id: "troubleshooting", title: "Troubleshooting" },
    ],
  },
  {
    id: "technical-reference",
    title: "Technical Reference",
    description: "Complete schemas, CLI commands, and specifications",
    sections: [
      { id: "reference-overview", title: "Overview" },
      { id: "components-reference", title: "Plugin Components" },
      { id: "installation-scopes", title: "Installation Scopes" },
      { id: "manifest-schema", title: "Plugin Manifest Schema" },
      { id: "caching-resolution", title: "Caching & File Resolution" },
      { id: "directory-structure", title: "Directory Structure" },
      { id: "cli-commands", title: "CLI Commands" },
      { id: "debugging", title: "Debugging & Tools" },
    ],
  },
  {
    id: "api-reference",
    title: "API Reference",
    description: "REST API for agents, scripts, and integrations",
    sections: [
      { id: "api-overview", title: "Overview" },
      { id: "api-auth", title: "Authentication" },
      { id: "api-marketplaces", title: "Custom Marketplaces" },
      { id: "api-marketplace-plugins", title: "Marketplace Plugins" },
      { id: "api-public", title: "Public Endpoints" },
      { id: "api-agent-workflow", title: "Agent Workflow" },
    ],
  },
  {
    id: "agent-api",
    title: "Agent API",
    description: "Complete API documentation for AI agents",
    sections: [
      { id: "agent-quick-start", title: "Quick Start" },
      { id: "agent-auth", title: "Auth Endpoints" },
      { id: "agent-plugins-public", title: "Plugins — Public" },
      { id: "agent-import", title: "Plugin Import" },
      { id: "agent-plugins-auth", title: "Plugins — Authenticated" },
      { id: "agent-favorites", title: "Favorites" },
      { id: "agent-marketplaces-auth", title: "Marketplaces — Auth" },
      { id: "agent-marketplaces-public", title: "Marketplaces — Public" },
      { id: "agent-packs", title: "Packs" },
      { id: "agent-skill-creator", title: "Skill Creator" },
      { id: "agent-workflow", title: "End-to-End Workflow" },
    ],
  },
  {
    id: "superbot-cards",
    title: "Superbot Cards",
    description: "Give your skill a dashboard card with settings, actions, and schedules",
    sections: [
      { id: "cards-overview", title: "What is a Superbot Card?" },
      { id: "cards-superbot-json", title: "The superbot.json File" },
      { id: "cards-quick-start", title: "Quick Start" },
      { id: "cards-card-section", title: "Card Section Reference" },
      { id: "cards-settings", title: "Settings Section" },
      { id: "cards-schedule", title: "Schedule Section" },
      { id: "cards-agent", title: "Agent Section" },
      { id: "cards-data-flow", title: "How Data Flows" },
      { id: "cards-full-example", title: "Full Example" },
      { id: "cards-api", title: "API Routes" },
      { id: "cards-migration", title: "Migrating from CARD.json" },
    ],
  },
];

export function DocsPage() {
  const [activeGroupId, setActiveGroupId] = useState<SectionGroupId>("core-concepts");
  const [activeSection, setActiveSection] = useState<Section>("what-is-plugin");

  const apiUrl =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.PROD ? window.location.origin : "http://localhost:3001");
  const marketplaceUrl = `${apiUrl}/api/marketplace.json`;

  const activeGroup = sectionGroups.find((g) => g.id === activeGroupId) || sectionGroups[0];

  const handleGroupChange = (groupId: SectionGroupId) => {
    setActiveGroupId(groupId);
    const group = sectionGroups.find((g) => g.id === groupId);
    if (group && group.sections.length > 0) {
      setActiveSection(group.sections[0].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
      </div>

      <div className="relative z-10 pt-20">
        {/* Top Sub-Navigation */}
        <div className="border-b border-elevated bg-base/80 backdrop-blur-sm">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-1 overflow-x-auto py-2">
              {sectionGroups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleGroupChange(group.id)}
                  className={`shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                    activeGroupId === group.id
                      ? "bg-amber/10 text-amber"
                      : "text-muted hover:bg-surface hover:text-secondary"
                  }`}
                >
                  {group.title}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex gap-12">
            {/* Sidebar Navigation - Only shows current group's sections */}
            <aside className="hidden w-56 shrink-0 lg:block">
              <div className="sticky top-28">
                <p className="mb-4 text-sm text-muted">{activeGroup.description}</p>
                <nav className="space-y-1">
                  {activeGroup.sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                        activeSection === section.id
                          ? "bg-amber/10 text-amber"
                          : "text-muted hover:bg-surface hover:text-secondary"
                      }`}
                    >
                      <ChevronRightIcon
                        size={14}
                        className={activeSection === section.id ? "text-amber" : "text-muted"}
                      />
                      {section.title}
                    </button>
                  ))}
                </nav>

                {activeGroupId === "marketplaces" && (
                  <div className="mt-6 rounded-xl border border-elevated bg-surface/50 p-4">
                    <p className="text-xs text-muted">This marketplace is available at:</p>
                    <code className="mt-2 block break-all font-mono text-xs text-amber">
                      {marketplaceUrl}
                    </code>
                  </div>
                )}

                {(activeGroupId === "api-reference" || activeGroupId === "agent-api") && (
                  <div className="mt-6 rounded-xl border border-elevated bg-surface/50 p-4">
                    <p className="text-xs text-muted">
                      {activeGroupId === "agent-api" ? "Agent docs endpoint:" : "Base API URL:"}
                    </p>
                    <code className="mt-2 block break-all font-mono text-xs text-amber">
                      {activeGroupId === "agent-api" ? `${apiUrl}/api/agent-docs` : apiUrl}
                    </code>
                  </div>
                )}
              </div>
            </aside>

            {/* Main Content */}
            <main className="min-w-0 flex-1">
              {/* Header */}
              <div className="mb-12">
                <div className="flex items-center gap-3 text-sm text-muted">
                  <Link to="/" className="hover:text-secondary">
                    Home
                  </Link>
                  <ChevronRightIcon size={14} />
                  <span>Documentation</span>
                  <ChevronRightIcon size={14} />
                  <span className="text-secondary">{activeGroup.title}</span>
                </div>
                <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-primary">
                  {activeGroup.title}
                </h1>
                <p className="mt-4 text-lg text-muted">{activeGroup.description}</p>
              </div>

              {/* Content Sections */}
              <div className="prose prose-invert prose-slate max-w-none">
                {activeGroupId === "core-concepts" && <CoreConceptsDocs />}
                {activeGroupId === "marketplaces" && (
                  <MarketplacesDocs marketplaceUrl={marketplaceUrl} />
                )}
                {activeGroupId === "technical-reference" && <TechnicalReferenceDocs />}
                {activeGroupId === "api-reference" && <ApiReferenceDocs apiUrl={apiUrl} />}
                {activeGroupId === "agent-api" && <AgentApiDocs apiUrl={apiUrl} />}
                {activeGroupId === "superbot-cards" && <SuperbotCardsDocs />}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
